
import Component, { mixins } from 'vue-class-component';
import { Emit, Prop } from 'vue-property-decorator';
import FormValidationMixin from '@/mixins/FormValidationMixin';
import FormInput from '@/components/form/FormInput.vue';
import { Validations } from 'vuelidate-property-decorators';
import {
    email,
    minLength,
    maxLength,
    required,
} from 'vuelidate/lib/validators';
import { SignupFormData } from '@/model/Signup';
import { emailLocalPartMaxLength } from '@/mixins/validations/email-validation';
import passwordValidations from '@/mixins/validations/password-validation';
import FileUsageType from '@/model/enums/FileUsageType';

@Component({
    components: {
        FormInput,
    },
})
export default class SignupForm extends mixins(FormValidationMixin) {
    @Prop({ type: Boolean, default: false })
    readonly showLegalConsents!: boolean;

    formData = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        passwordAgain: '',
        acceptedTerms: false,
        acceptedPrivacyPolicy: false,
    } as SignupFormData;

    submitted = false;

    get termsAndConditionsLink(): string {
        return this.getSystemDocumentLink(FileUsageType.GTC);
    }

    get privacyPolicyLink(): string {
        return this.getSystemDocumentLink(FileUsageType.GDPR);
    }

    getSystemDocumentLink(usageType: FileUsageType): string {
        const baseUrl = (process.env.VUE_APP_BASE_URL || '').replace(
            /\/+$/,
            '',
        );

        return `${baseUrl}/documents/${usageType}/view`;
    }

    @Validations()
    validations = {
        formData: {
            firstName: {
                required,
                maxLength: maxLength(20),
            },
            lastName: {
                required,
                maxLength: maxLength(20),
            },
            email: {
                email,
                required,
                maxLength: maxLength(80),
                localPartMaxLength: (value: string): boolean =>
                    emailLocalPartMaxLength(value),
            },
            phone: {
                required,
                minLength: minLength(6),
                maxLength: maxLength(15),
                phoneNumber: (value: string): boolean => {
                    return value.length
                        ? /^[+]{0,1}[-\s0-9]*$/.test(value)
                        : true;
                },
            },
            acceptedTerms: {
                accepted: (value: boolean): boolean => {
                    return !this.showLegalConsents || value === true;
                },
            },
            acceptedPrivacyPolicy: {
                accepted: (value: boolean): boolean => {
                    return !this.showLegalConsents || value === true;
                },
            },
            ...passwordValidations,
        },
    };

    @Emit()
    submit(): SignupFormData {
        return this.formData;
    }

    getValidationMessages(name: string): string[] {
        const validationMessages = this.error?.validationMessages?.[name];

        if (!validationMessages) {
            return [];
        }

        return typeof validationMessages === 'string'
            ? [validationMessages]
            : validationMessages;
    }

    onSubmit(): void {
        this.submitted = true;

        this.$v.formData.$touch();
        if (!this.$v.$invalid) {
            this.submit();
        }
    }
}
